import type { FC, Ref } from 'react';
import { useEffect, useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { Button, Link } from 'platform/atoms';
import { useAuthContext } from 'platform/components/AuthContext';
import { HOMEPAGE_URL } from 'shared/helpers/globals';
import { OpendoorDashboardLogo, OpendoorMobileNavLogo } from 'shared/helpers/svgCollection';

import './sellExperienceNavigation.css';

const DEFAULT_USER_INITIAL = 'O';
const DESKTOP_NAV_QUERY = '(min-width: 768px)';
export type NavigationButtonElement = HTMLButtonElement | HTMLAnchorElement;

/**
 * Inner/wizard page header metadata. When passed to the shared layout the app-bar switches from
 * the top-level nav (logo + address + account menu) to a focused header: back · title/subtitle ·
 * avatar. `backHref` always points one level up (never browser back), so deep links stay safe.
 */
export interface PageHeader {
  /** Optional — omit for a back-arrow-only bar (e.g. wizard pages that title themselves in the body). */
  title?: string;
  subtitle?: string;
  backHref: string;
}

interface NavigationBarProps {
  handleOpen: () => void;
  handleClose?: () => void;
  handleToggleDesktopMenu?: () => void;
  isMenuOpen?: boolean;
  isDesktopMenuOpen?: boolean;
  applySubpageTheme?: boolean;
  /** Street of the address the seller is currently viewing; shown beside the avatar on desktop. */
  currentAddress?: string;
  /** When present, render the focused inner-page header instead of the top-level nav. */
  pageHeader?: PageHeader;
  menuButtonRef?: Ref<NavigationButtonElement>;
  mobileMenuButtonRef?: Ref<NavigationButtonElement>;
}

export const NavigationBar: FC<NavigationBarProps> = ({
  handleOpen,
  handleClose,
  handleToggleDesktopMenu,
  isMenuOpen = false,
  isDesktopMenuOpen = false,
  applySubpageTheme,
  currentAddress,
  pageHeader,
  menuButtonRef,
  mobileMenuButtonRef,
}) => {
  const { user } = useAuthContext();

  // Trailing account menu — shared by the top-level nav and the focused inner header so both expose
  // the same address switcher / account links (the avatar opens it on desktop, the hamburger on
  // mobile). The current-address label only shows in the top-level nav.
  const accountMenu = (
    <Row className="items-center gap-6">
      {!pageHeader && currentAddress ? (
        <Text
          as="span"
          className="sell-experience-navigation-desktop-only max-w-70 truncate text-label-md text-od-content-primary"
        >
          {currentAddress}
        </Text>
      ) : null}
      <Button
        ref={menuButtonRef}
        aria-controls="desktop-account-menu"
        aria-label={isDesktopMenuOpen ? 'Close the account menu' : 'Open the account menu'}
        aria-expanded={isDesktopMenuOpen}
        analyticsName="open-navigation-menu"
        onClick={handleToggleDesktopMenu ?? handleOpen}
        variant="secondaryInverse"
        size="lg"
        // The avatar is a fixed 40px circle: `size-10` overrides the `lg` button's `h-14` so the
        // button matches the circle (otherwise the hover state paints an oversized pill behind it),
        // and `!bg-transparent` suppresses the variant's hover/active fill in favor of the ring below.
        className="sell-experience-navigation-desktop-only size-10 items-center justify-center rounded-full border-0 !bg-transparent p-0 transition-shadow duration-200 hover:ring-2 hover:ring-od-border-primary hover:ring-offset-2"
      >
        <Text
          as="span"
          className="inline-flex size-10 items-center justify-center rounded-full bg-od-bg-inverse-primary text-label-md text-od-content-inverse-primary"
        >
          {getInitials(user?.fullName)}
        </Text>
      </Button>
      <Button
        ref={mobileMenuButtonRef}
        analyticsName="open-navigation-menu"
        aria-controls="sell-experience-mobile-navigation"
        aria-expanded={isMenuOpen}
        aria-label={isMenuOpen ? 'Close the navigation menu' : 'Open the navigation menu'}
        onClick={isMenuOpen ? (handleClose ?? handleOpen) : handleOpen}
        variant="secondary"
        size="lg"
        className="sell-experience-navigation-mobile-only size-10 items-center justify-center rounded-full border-0 bg-od-bg-primary p-0 text-od-content-primary"
      >
        <MobileMenuIcon isOpen={isMenuOpen} />
      </Button>
    </Row>
  );

  return (
    <Row
      as="nav"
      className={cn(
        'sell-experience-navigation-bar sticky top-0 z-[1001] h-16 w-full items-center bg-od-bg-primary px-4 py-0 text-od-content-primary md:h-auto md:px-8 md:py-4',
        pageHeader ? 'gap-3' : 'justify-between',
        applySubpageTheme ? 'md:bg-od-bg-secondary' : 'md:bg-od-bg-primary',
      )}
    >
      {pageHeader ? (
        <>
          <Link
            href={pageHeader.backHref}
            aria-label="Go back"
            analyticsName="inner-page-header-back"
            className="inline-flex size-10 shrink-0 items-center justify-center rounded-full border-0 bg-od-bg-primary p-0 text-od-content-primary no-underline hover:bg-od-bg-hover-primary"
          >
            <BackIcon />
          </Link>
          <Col className="min-w-0 flex-1 items-center">
            {pageHeader.title ? (
              <Text
                as="h1"
                className="max-w-full truncate text-center text-label-lg text-od-content-primary"
              >
                {pageHeader.title}
              </Text>
            ) : null}
            {pageHeader.subtitle ? (
              <Text
                as="span"
                className="max-w-full truncate text-center text-body-sm text-od-content-tertiary"
              >
                {pageHeader.subtitle}
              </Text>
            ) : null}
          </Col>
        </>
      ) : (
        <OpendoorHomeLink />
      )}
      {accountMenu}
    </Row>
  );
};

const BackIcon: FC = () => (
  <svg
    aria-hidden="true"
    height="24"
    width="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="text-current"
  >
    <path
      d="M15 18L9 12L15 6"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const getIsDesktopNavigation = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.matchMedia(DESKTOP_NAV_QUERY).matches;
};

export const useIsDesktopNavigation = () => {
  const [isDesktopNavigation, setIsDesktopNavigation] = useState(getIsDesktopNavigation);

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_NAV_QUERY);
    const handleChange = () => {
      setIsDesktopNavigation(mediaQuery.matches);
    };

    handleChange();
    mediaQuery.addEventListener('change', handleChange);
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return isDesktopNavigation;
};

const MobileMenuIcon: FC<{ isOpen: boolean }> = ({ isOpen }) => (
  <svg
    aria-hidden="true"
    height="24"
    viewBox="0 0 24 24"
    width="24"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    className="text-current"
  >
    <path
      d={isOpen ? 'M6 6L18 18' : 'M4 8H20'}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      className="sell-experience-mobile-menu-icon-line"
    />
    <path
      d={isOpen ? 'M18 6L6 18' : 'M4 16H20'}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      className="sell-experience-mobile-menu-icon-line"
    />
  </svg>
);

// Desktop and mobile render different logos but the same destination — keep the label, route, and
// analytics name in one place so they can't drift apart. The logo points at the marketing homepage
// (its conventional destination); the account menu's address rows are how sellers reach a specific
// property dashboard.
const HOME_LINK_PROPS = {
  'aria-label': 'Go back to our landing page',
  analyticsName: 'Navigate to homepage',
  href: HOMEPAGE_URL,
} as const;

export const OpendoorHomeLink: FC = () => {
  return (
    <>
      <Link
        {...HOME_LINK_PROPS}
        className="sell-experience-navigation-desktop-only text-inherit no-underline"
      >
        <OpendoorDashboardLogo />
      </Link>
      <Link
        {...HOME_LINK_PROPS}
        className="sell-experience-navigation-mobile-only text-inherit no-underline"
      >
        <OpendoorMobileNavLogo color="black" />
      </Link>
    </>
  );
};

export const getInitials = (fullName: string | null | undefined) => {
  const parts = fullName?.trim().split(/\s+/).filter(Boolean) ?? [];
  const firstInitial = parts[0]?.[0] ?? DEFAULT_USER_INITIAL;
  const lastInitial = parts.length > 1 ? parts[parts.length - 1]?.[0] : undefined;

  return `${firstInitial}${lastInitial ?? ''}`.toUpperCase();
};
